/**
 * Media With Tabs
 * Lazy-load Shopify hosted videos.
 */

if (!customElements.get('media-with-tabs')) {
  class MediaWithTabs extends HTMLElement {
    constructor() {
      super();

      this.tabClickHandlers = [];
      this.videoLoadObserver = null;
    }

    connectedCallback() {
      this.tabs = Array.from(
        this.querySelectorAll('.media-with-tabs--tab')
      );

      this.slider = this.querySelector('slide-show');

      this.setupTabs();
      this.setupThemeEditor();
      this.setupVideoControls();
      this.setupLazyVideos();
    }

    /**
     * Bind tab click events.
     */
    setupTabs() {
      this.tabs.forEach((tab, index) => {
        const handler = () => {
          this.tabClick(tab, index);
        };

        this.tabClickHandlers.push({
          tab,
          handler
        });

        tab.addEventListener('click', handler);
      });
    }

    /**
     * Shopify theme editor support.
     */
    setupThemeEditor() {
      this.onBlockSelectBound = (event) => {
        const tab = event.target.closest(
          '.media-with-tabs--tab'
        );

        if (!tab) return;

        const index = this.tabs.indexOf(tab);

        if (index > -1) {
          this.tabClick(tab, index);
        }
      };

      this.addEventListener(
        'shopify:block:select',
        this.onBlockSelectBound
      );
    }

    /**
     * Play / pause controls.
     */
    setupVideoControls() {
      this.querySelectorAll(
        '.media-with-tabs--media--controls button'
      ).forEach((toggle) => {
        toggle.addEventListener('click', (event) => {
          event.stopPropagation();

          const container = toggle.closest(
            '.media-with-tabs--media--video'
          );

          const video = container?.querySelector('video');

          if (!video) return;

          this.loadVideo(video);

          if (video.paused) {
            const playPromise = video.play();

            if (playPromise instanceof Promise) {
              playPromise.catch(() => {});
            }

            toggle.classList.remove('paused');
          } else {
            video.pause();
            toggle.classList.add('paused');
          }
        });
      });
    }

    /**
     * Load only the first video when this section
     * approaches the viewport.
     */
    setupLazyVideos() {
      const videos = Array.from(
        this.querySelectorAll(
          '.media-with-tabs--lazy-video'
        )
      );

      if (!videos.length) return;

      const firstVideo = videos[0];

      if (!('IntersectionObserver' in window)) {
        this.loadVideo(firstVideo);
        return;
      }

      this.videoLoadObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            this.loadVideo(firstVideo);

            this.videoLoadObserver.disconnect();
            this.videoLoadObserver = null;
          });
        },
        {
          rootMargin: '600px 0px',
          threshold: 0
        }
      );

      this.videoLoadObserver.observe(this);
    }

    /**
     * Convert source[data-src] to source[src].
     */
    loadVideo(video) {
      if (!video) return;

      if (video.dataset.loaded === 'true') {
        return;
      }

      const sources = video.querySelectorAll(
        'source[data-src]'
      );

      sources.forEach((source) => {
        const src = source.dataset.src;

        if (!src) return;

        source.src = src;
        source.removeAttribute('data-src');
      });

      video.dataset.loaded = 'true';

      video.load();
    }

    /**
     * Handle tab selection.
     */
    tabClick(tab, index) {
      const flkty = Flickity.data(this.slider);

      if (!flkty) return;

      this.tabs.forEach((item) => {
        item.classList.remove('active');
      });

      tab.classList.add('active');

      const targetCell = flkty.cells[index];

      const targetVideo = targetCell?.element.querySelector(
        '.media-with-tabs--lazy-video'
      );

      if (targetVideo) {
        this.loadVideo(targetVideo);
      }

      flkty.select(index);
    }

    disconnectedCallback() {
      if (this.tabClickHandlers) {
        this.tabClickHandlers.forEach(
          ({ tab, handler }) => {
            tab.removeEventListener(
              'click',
              handler
            );
          }
        );
      }

      if (this.onBlockSelectBound) {
        this.removeEventListener(
          'shopify:block:select',
          this.onBlockSelectBound
        );
      }

      if (this.videoLoadObserver) {
        this.videoLoadObserver.disconnect();
        this.videoLoadObserver = null;
      }
    }
  }

  customElements.define(
    'media-with-tabs',
    MediaWithTabs
  );
}